/**
 * ChatWindow - Real-time chat component with  branding
 * Phase 11 Part 2: WebSocket-based Messaging System
 * 
 * Design specs:
 * - Customer: White bg, Green border, Black text
 * - Driver: Solid Green bg, White text
 * - Merchant: Yellow bg, Black text
 * - Admin/Support: Black bg, White text, Yellow outline
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import { useWebSocket, buildChatWsUrl } from '../hooks/useWebSocket';
import { 
  Send, 
  Paperclip, 
  Check, 
  CheckCheck,
  User,
  Truck,
  Store,
  Shield,
  ArrowLeft,
  MoreVertical,
  Loader2,
  Wifi,
  WifiOff,
  Smile
} from 'lucide-react';

// Role icon mapping
const ROLE_ICONS = {
  customer: User,
  driver: Truck,
  merchant: Store,
  admin: Shield,
  support: Shield
};

// Role color mapping ( Official Brand - from user specs)
const ROLE_COLORS = {
  customer: {
    bg: 'bg-white',
    border: 'border-[#00A86B]',
    text: 'text-black',
    shadow: 'shadow-sm'
  },
  driver: {
    bg: 'bg-[#00A86B]',
    border: 'border-[#00A86B]',
    text: 'text-white',
    shadow: 'shadow-md'
  },
  merchant: {
    bg: 'bg-[#FFD300]',
    border: 'border-[#FFD300]',
    text: 'text-black',
    shadow: 'shadow-sm'
  },
  admin: {
    bg: 'bg-black',
    border: 'border-[#FFD300]',
    text: 'text-white',
    shadow: 'shadow-md'
  },
  support: {
    bg: 'bg-black',
    border: 'border-[#FFD300]',
    text: 'text-white',
    shadow: 'shadow-md'
  }
};

// Role badge colors
const ROLE_BADGE_COLORS = {
  customer: 'bg-white text-[#00A86B] border border-[#00A86B]',
  driver: 'bg-[#00A86B] text-white',
  merchant: 'bg-[#FFD300] text-black',
  admin: 'bg-black text-white border border-[#FFD300]',
  support: 'bg-black text-white border border-[#FFD300]'
};

export default function ChatWindow({ 
  conversationId, 
  title = 'Chat',
  onBack,
  compact = false,
  className = ''
}) {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [conversation, setConversation] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  // Build WebSocket URL
  const wsUrl = buildChatWsUrl(conversationId, token);

  // WebSocket connection
  const { 
    isConnected, 
    sendMessage: wsSendMessage, 
    sendTyping,
    markRead,
    reconnectAttempts 
  } = useWebSocket(wsUrl, {
    enabled: !!conversationId && !!token,
    onMessage: (data) => {
      switch (data.type) {
        case 'new_message':
          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, { ...data.message, is_own: data.message.sender_id === user?.id }];
          });
          // Mark as read if window is focused
          if (document.hasFocus()) {
            markRead();
          }
          break;
        
        case 'typing':
          if (data.user_id !== user?.id) {
            setTypingUsers(prev => ({
              ...prev,
              [data.user_id]: data.is_typing ? data.user_name : null
            }));
            // Auto-clear typing after 5s
            if (data.is_typing) {
              setTimeout(() => {
                setTypingUsers(prev => ({
                  ...prev,
                  [data.user_id]: null
                }));
              }, 5000);
            }
          }
          break;
        
        case 'read_receipt':
          setMessages(prev => prev.map(msg => ({
            ...msg,
            read_by: [...new Set([...(msg.read_by || []), data.user_id])]
          })));
          break;
        
        case 'user_joined':
        case 'user_left':
        case 'connected':
          setOnlineUsers(data.online_users || []);
          break;
        
        default:
          break;
      }
    },
    onConnect: () => {
      console.log('[Chat] WebSocket connected');
    },
    onDisconnect: () => {
      console.log('[Chat] WebSocket disconnected');
    }
  });

  // Fetch messages (initial load + fallback)
  const fetchMessages = useCallback(async () => {
    try {
      const params = new URLSearchParams({ conversation_id: conversationId });
      const res = await api.get(`/messaging/messages?${params}`);
      setMessages(res.data.messages.map(m => ({
        ...m,
        is_own: m.sender_id === user?.id
      })));
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [conversationId, user?.id]);

  // Fetch conversation details
  const fetchConversation = useCallback(async () => {
    try {
      const res = await api.get(`/messaging/conversations/${conversationId}`);
      setConversation(res.data);
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    }
  }, [conversationId]);

  // Initial load
  useEffect(() => {
    if (conversationId) {
      fetchConversation();
      fetchMessages();
    }
  }, [conversationId, fetchConversation, fetchMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!isConnected) return;
    
    sendTyping(true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing after 2s of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(false);
    }, 2000);
  }, [isConnected, sendTyping]);

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    const text = newMessage.trim();
    setNewMessage('');
    
    // Clear typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    sendTyping(false);
    
    // Try WebSocket first
    if (isConnected) {
      const sent = wsSendMessage(text);
      if (sent) return;
    }
    
    // Fallback to HTTP
    try {
      const res = await api.post('/messaging/messages', {
        conversation_id: conversationId,
        text
      });
      setMessages(prev => [...prev, { ...res.data.message, is_own: true }]);
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(text); // Restore message
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Get typing indicator text
  const getTypingText = () => {
    const typing = Object.entries(typingUsers)
      .filter(([_, name]) => name)
      .map(([_, name]) => name);
    
    if (typing.length === 0) return null;
    if (typing.length === 1) return `${typing[0]} is typing...`;
    if (typing.length === 2) return `${typing[0]} and ${typing[1]} are typing...`;
    return `${typing.length} people are typing...`;
  };

  // Render message bubble with 3D effect
  const renderMessage = (msg, index) => {
    const isOwn = msg.is_own || msg.sender_id === user?.id;
    const role = msg.sender_role || 'customer';
    const colors = ROLE_COLORS[role] || ROLE_COLORS.customer;
    const badgeColor = ROLE_BADGE_COLORS[role] || ROLE_BADGE_COLORS.customer;
    const RoleIcon = ROLE_ICONS[role] || User;
    
    // Check if same sender as previous
    const prevMsg = messages[index - 1];
    const sameSender = prevMsg && prevMsg.sender_id === msg.sender_id;
    
    // Check if read by others
    const readByOthers = (msg.read_by || []).filter(id => id !== msg.sender_id).length > 0;

    return (
      <div
        key={msg.id}
        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${sameSender ? 'mt-1' : 'mt-4'} animate-fade-slide-up`}
      >
        {/* Avatar (only for first message in group, non-own messages) */}
        {!isOwn && !sameSender && (
          <div 
            className={`w-9 h-9 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center mr-2 flex-shrink-0 ${colors.shadow}`}
            style={{ transform: 'translateY(2px)' }}
          >
            <RoleIcon className={`w-4 h-4 ${colors.text}`} />
          </div>
        )}
        {!isOwn && sameSender && <div className="w-11" />}

        <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
          {/* Sender name (only for first message in group) */}
          {!sameSender && (
            <div className={`flex items-center gap-2 mb-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <span className="text-xs text-gray-500">
                {msg.sender_name || 'Unknown'}
              </span>
              <Badge className={`text-[10px] py-0 px-1.5 ${badgeColor}`}>
                {role}
              </Badge>
            </div>
          )}

          {/* Message bubble with 3D effect */}
          <div
            className={`
              px-4 py-2.5 border-2 transition-all duration-200
              ${isOwn 
                ? `${colors.bg} ${colors.border} ${colors.text} rounded-2xl rounded-br-md ${colors.shadow}` 
                : `bg-gray-100 border-gray-200 text-gray-800 rounded-2xl rounded-bl-md shadow-sm`
              }
              ${role === 'driver' && isOwn ? 'transform -rotate-[0.5deg]' : ''}
            `}
            style={{
              boxShadow: isOwn && role === 'driver' 
                ? '0 4px 6px -1px rgba(0, 168, 107, 0.3), 0 2px 4px -1px rgba(0, 168, 107, 0.2)'
                : undefined
            }}
          >
            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.text}</p>
            
            {/* Attachments */}
            {msg.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {msg.attachments.map((url, i) => (
                  <img 
                    key={i}
                    src={url} 
                    alt="attachment" 
                    className="w-24 h-24 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-all hover:scale-105 shadow-md"
                    onClick={() => window.open(url, '_blank')}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Time & read status */}
          <div className={`flex items-center gap-1.5 mt-1 text-xs text-gray-400 ${isOwn ? 'justify-end' : ''}`}>
            <span>{formatTime(msg.created_at)}</span>
            {isOwn && (
              readByOthers 
                ? <CheckCheck className="w-3.5 h-3.5 text-[#00A86B]" />
                : <Check className="w-3.5 h-3.5" />
            )}
          </div>
        </div>

        {/* Own avatar */}
        {isOwn && !sameSender && (
          <div 
            className={`w-9 h-9 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center ml-2 flex-shrink-0 ${colors.shadow}`}
            style={{ transform: 'translateY(2px)' }}
          >
            <RoleIcon className={`w-4 h-4 ${colors.text}`} />
          </div>
        )}
        {isOwn && sameSender && <div className="w-11" />}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  const typingText = getTypingText();

  return (
    <div className={`flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden ${compact ? 'h-96' : 'h-[600px]'} ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#00A86B] to-[#00A86B]/90 text-white">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} className="text-white hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div className="flex-1">
          <h3 className="font-semibold">{title || conversation?.title || 'Chat'}</h3>
          <div className="flex items-center gap-2 text-xs text-white/70">
            <span>{conversation?.participants?.length || 0} participants</span>
            {isConnected ? (
              <span className="flex items-center gap-1 text-white/90">
                <Wifi className="w-3 h-3" />
                Live
              </span>
            ) : (
              <span className="flex items-center gap-1 text-white/50">
                <WifiOff className="w-3 h-3" />
                {reconnectAttempts > 0 ? 'Reconnecting...' : 'Offline'}
              </span>
            )}
          </div>
        </div>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
          <MoreVertical className="w-5 h-5" />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-50 to-white">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Send className="w-12 h-12 mb-2 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => renderMessage(msg, index))}
            <div ref={messagesEndRef} />
          </>
        )}
        
        {/* Typing indicator */}
        {typingText && (
          <div className="flex items-center gap-2 mt-4 text-gray-500 text-sm animate-pulse">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-[#00A86B] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-[#00A86B] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-[#00A86B] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span>{typingText}</span>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 bg-white border-t border-gray-200">
        <div className="flex items-center gap-2">
          <Button 
            type="button" 
            variant="ghost" 
            size="icon" 
            className="text-gray-400 hover:text-[#00A86B]"
          >
            <Paperclip className="w-5 h-5" />
          </Button>
          <Button 
            type="button" 
            variant="ghost" 
            size="icon" 
            className="text-gray-400 hover:text-[#00A86B]"
          >
            <Smile className="w-5 h-5" />
          </Button>
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Type a message..."
              className={`w-full bg-gray-50 border-2 transition-all duration-200 ${
                isFocused 
                  ? 'border-[#00A86B] ring-2 ring-[#00A86B]/20' 
                  : 'border-gray-200'
              }`}
            />
          </div>
          <Button 
            type="submit" 
            size="icon" 
            disabled={!newMessage.trim()}
            className="bg-[#FFD300] hover:bg-[#FFD300]/90 text-black disabled:opacity-50 transition-all duration-200 hover:scale-105"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </form>

      {/* CSS for animations */}
      <style>{`
        @keyframes fade-slide-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-slide-up {
          animation: fade-slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
